import React, { useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { ILandingPageBuilderForm } from '../../types';
import { FormSection } from './shared/FormSection';
import { FormContainer } from './shared/FormContainer';
import { FormikSwitchField } from 'lib/components/formik/FormikSwitchField';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import {
  FontCases,
  FontSizes,
  LP_BUILDER_FORM_MODE,
  LpBuilderFormMode,
} from '../../constants';
import { FormikNumberField } from 'lib/components/formik/FormikNumberField';
import { FontFamilyOption } from 'app/pages/design/landingPageBuilder/components';
import { getFilteredFontWeights } from 'lib/utils/getFilteredFontWeights';
import { FormikColorPicker } from 'lib/components/formik/FormikColorPicker';
import { useCovideoTheme } from 'react-covideo-common';
import { ILandingPageBuilderBulkEditFormData } from '../../helpers/getInitialLandingPageValuesForBulkEdit';

interface ICTAFormProps {
  allFontFamilyOptions: FontFamilyOption[];
  mode: LpBuilderFormMode;
}

export const CTAForm = ({ allFontFamilyOptions, mode }: ICTAFormProps) => {
  const themes = useCovideoTheme();
  const { values, setFieldValue } = useFormikContext<
    ILandingPageBuilderForm | ILandingPageBuilderBulkEditFormData
  >();
  const isBulkEditMode = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;

  const { filteredFontWeights, fallbackWeight } = useMemo(() => {
    return getFilteredFontWeights({
      fontFamilyValue: values.buttonFontFamily,
      fontWeightValue: values.buttonFontWeight,
      allFontFamilyOptions,
    });
  }, [values.buttonFontFamily]);

  useEffect(() => {
    // if we are in bulk edit mode and there are no font weights available — it means the font family has been deselected
    // set the font weight to null
    if (!isBulkEditMode) return;
    if (!filteredFontWeights.length) {
      setFieldValue('buttonFontWeight', null);
    }
  }, [filteredFontWeights]);

  useEffect(() => {
    if (fallbackWeight) {
      setFieldValue('buttonFontWeight', fallbackWeight);
    }
  }, [fallbackWeight]);

  const fontSizeOptions = isBulkEditMode
    ? [{ label: 'None', value: null }, ...FontSizes]
    : FontSizes;

  const fontCasesOptions = isBulkEditMode
    ? [{ label: 'None', value: null }, ...FontCases]
    : FontCases;

  const onFormikNumberInputBlur = (field: keyof ILandingPageBuilderForm) => {
    // do not use custom blur if not in bulk edit mode
    if (!isBulkEditMode) return;
    if (values[field] === '') {
      setFieldValue(field, null);
    }
  };

  const handleColorPickerInputChange = (
    field: keyof ILandingPageBuilderBulkEditFormData,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const hex = e.target.value;
    if (isBulkEditMode) {
      // in bulk edit mode, allow empty value
      if (hex === '') {
        setFieldValue(field, null);
        return;
      }
    }
  };

  const handleShowButtonBorderChange = () => {
    const newValue = values.showButtonBorder ? 0 : 1;
    // if we are in bulk edit mode, set the buttonBorderColor & buttonBorderHoverColor to null to avoid showing values in changes summary
    if (isBulkEditMode && newValue === 0) {
      setFieldValue('buttonBorderColor', null);
      setFieldValue('buttonBorderHoverColor', null);
    }
    setFieldValue('showButtonBorder', newValue);
  };

  return (
    <FormContainer title='CTAs'>
      <FormSection title='Typography'>
        <FormikDropdownField
          options={allFontFamilyOptions}
          name={'buttonFontFamily'}
          creatable={false}
          placeholder='Font'
          label={'Select a Font'}
          isRequired={false}
          disabled={false}
          hideError
        />
        <FormikDropdownField
          options={fontSizeOptions}
          name={'buttonFontSize'}
          creatable={false}
          placeholder='Font Size'
          label='Font Size'
          isRequired={false}
          disabled={false}
          extendStyles={{
            mainWrapper: {
              minWidth: '131px',
              width: '100%',
            },
            labelStyles: {
              fontSize: '14px',
            },
          }}
          hideError
        />
        <FormikDropdownField
          options={filteredFontWeights}
          name={'buttonFontWeight'}
          creatable={false}
          placeholder='Font Weight'
          label='Font Weight'
          isRequired={false}
          disabled={false}
          extendStyles={{
            mainWrapper: {
              minWidth: '131px',
            },
            labelStyles: {
              fontSize: '14px',
            },
          }}
          hideError
        />
        <FormikDropdownField
          name='buttonFontCase'
          options={fontCasesOptions}
          label='Case'
          disabled={false}
          hideError
        />
      </FormSection>
      <FormSection title='Shape'>
        <FormikNumberField
          label='Button Corner Radius'
          name='buttonCornerRadius'
          suffix='px'
          min={0}
          isRequired={false}
          hideError
          onBlur={() => onFormikNumberInputBlur('buttonCornerRadius')}
        />
      </FormSection>
      <FormSection title='Button color'>
        <FormikColorPicker
          label='Button Color'
          name='buttonColor'
          extendStyles={{
            labelStyles: {
              fontSize: '12px',
              color: themes.colors.neutral[80],
              fontWeight: 600,
              marginBottom: '8px',
            },
          }}
          onInputChange={e => handleColorPickerInputChange('buttonColor', e)}
        />
        <FormikColorPicker
          label='Button Color Hover'
          name='buttonHoverColor'
          extendStyles={{
            labelStyles: {
              fontSize: '12px',
              color: themes.colors.neutral[80],
              fontWeight: 600,
              marginBottom: '8px',
            },
          }}
          onInputChange={e =>
            handleColorPickerInputChange('buttonHoverColor', e)
          }
        />
      </FormSection>
      <FormSection title='Button border color'>
        <FormikSwitchField
          name='button-border'
          onChange={handleShowButtonBorderChange}
          label='Button Border'
          extendStyles={{
            labelStyles: {
              fontSize: '15px',
              lineHeight: '24px',
              fontWeight: 500,
              color: themes.colors.neutral[100],
            },
          }}
          checked={!!values.showButtonBorder}
        />
        {!!values.showButtonBorder ? (
          <>
            <FormikColorPicker
              label='Button Border Color'
              name='buttonBorderColor'
              extendStyles={{
                labelStyles: {
                  fontSize: '12px',
                  color: themes.colors.neutral[80],
                  fontWeight: 600,
                  marginBottom: '8px',
                },
              }}
              onInputChange={e =>
                handleColorPickerInputChange('buttonBorderColor', e)
              }
            />
            <FormikColorPicker
              label='Button Border Hover Color'
              name='buttonBorderHoverColor'
              extendStyles={{
                labelStyles: {
                  fontSize: '12px',
                  color: themes.colors.neutral[80],
                  fontWeight: 600,
                  marginBottom: '8px',
                },
              }}
              onInputChange={e =>
                handleColorPickerInputChange('buttonBorderHoverColor', e)
              }
            />
          </>
        ) : (
          <></>
        )}
      </FormSection>
      <FormSection title='Button text color'>
        <FormikColorPicker
          label='Button Text Color'
          name='buttonTextColor'
          extendStyles={{
            labelStyles: {
              fontSize: '12px',
              color: themes.colors.neutral[80],
              fontWeight: 600,
              marginBottom: '8px',
            },
          }}
          onInputChange={e =>
            handleColorPickerInputChange('buttonTextColor', e)
          }
        />
        <FormikColorPicker
          label='Button Text Color Hover'
          name='buttonTextHoverColor'
          extendStyles={{
            labelStyles: {
              fontSize: '12px',
              color: themes.colors.neutral[80],
              fontWeight: 600,
              marginBottom: '8px',
            },
          }}
          onInputChange={e =>
            handleColorPickerInputChange('buttonTextHoverColor', e)
          }
        />
      </FormSection>
      <FormSection title='Button Padding'>
        <FormikNumberField
          label='Top/Bottom'
          name='buttonPaddingY'
          suffix='px'
          min={0}
          isRequired={false}
          hideError
          onBlur={() => onFormikNumberInputBlur('buttonPaddingY')}
        />
        <FormikNumberField
          label='Left/Right'
          name='buttonPaddingX'
          suffix='px'
          min={0}
          isRequired={false}
          hideError
          onBlur={() => onFormikNumberInputBlur('buttonPaddingX')}
        />
      </FormSection>
    </FormContainer>
  );
};
